import { AnimatePresence, motion } from 'framer-motion';
import { ShieldOff, Trash2, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';

import LoadingSpinner from '../../../../components/LoadingSpinner';

type TakedownDialogProps = {
  isOpen: boolean;
  jobTitle: string;
  company: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
};

const TakedownDialog: React.FC<TakedownDialogProps> = ({
  isOpen,
  jobTitle,
  company,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!isOpen) {
      setIsProcessing(false);
      setError(null);
    }
  }, [isOpen]);

  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isProcessing) onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleEscape);
      return () => window.removeEventListener('keydown', handleEscape);
    }
    return undefined;
  }, [isOpen, onClose, isProcessing]);

  const handleConfirm = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      await onConfirm();
    } catch (e) {
      setError((e as Error).message ?? t('dashboard.jobs.takedown.failed', 'Takedown failed.'));
      setIsProcessing(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={isProcessing ? undefined : onClose}
          style={{ pointerEvents: 'all' }}
        >
          <motion.div
            initial={{ scale: 0.96, opacity: 0, y: 6 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.96, opacity: 0, y: 6 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            className="ds-card-pop w-full max-w-sm mx-4 overflow-y-auto max-h-[90vh] p-0"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 py-2 shrink-0 select-none"
              style={{ background: 'var(--ds-danger)' }}>
              <div className="flex items-center gap-2">
                <ShieldOff className="w-4 h-4 text-white/80" />
                <span className="t-card-title" style={{ color: 'var(--ds-text-on-brand)' }}>
                  {t('dashboard.jobs.takedown.title', 'Take Down Job')}
                </span>
              </div>
              {!isProcessing && (
                <button
                  type="button"
                  onClick={onClose}
                  className="min-w-[44px] min-h-[44px] w-6 h-6 flex items-center justify-center rounded text-white/70 hover:text-white hover:bg-white/20 transition-colors touch-manipulation"
                  aria-label={t('layout.close', 'Close')}
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            <div className="p-4 space-y-4">
              <div className="space-y-1.5">
                <p className="t-card-title">
                  {t('dashboard.jobs.takedown.confirmHeading', 'Mark this job as inactive?')}
                </p>
                <p className="t-body">
                  {t('dashboard.jobs.takedown.confirmBody', {
                    defaultValue: '"{{title}}" at {{company}} will be hidden from all users. You can restore it via the database.',
                    title: jobTitle,
                    company,
                  })}
                </p>
              </div>

              {error && (
                <p className="text-xs text-red-500 bg-red-500/10 rounded-lg px-3 py-2" role="alert">
                  {error}
                </p>
              )}

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={isProcessing}
                  className="ds-btn ds-btn-danger flex-1 min-h-[44px] touch-manipulation disabled:opacity-50"
                >
                  {isProcessing ? (
                    <>
                      <LoadingSpinner size="xs" showText={false} />
                      {t('dashboard.jobs.takedown.processing', 'Taking down…')}
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-3.5 h-3.5 shrink-0" />
                      {t('dashboard.jobs.takedown.confirm', 'Take down')}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isProcessing}
                  className="ds-btn ds-btn-secondary flex-1 min-h-[44px] touch-manipulation disabled:opacity-50"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
};

export default React.memo(TakedownDialog);
